import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { ThumbsUp, ThumbsDown, Flag, Check } from "lucide-react";
import { toast } from "sonner";

export const FEEDBACK_OPTIONS = [
  { id: "helpful", label: "Helpful", icon: ThumbsUp },
  { id: "not_helpful", label: "Not helpful", icon: ThumbsDown },
  { id: "citation_incorrect", label: "Citation incorrect" },
  { id: "source_outdated", label: "Source may be outdated" },
  { id: "policy_missed", label: "Organizational policy was missed" },
  { id: "unsafe_biased", label: "Report unsafe or biased response" },
];

export default function FeedbackBar({ responseId, initial, onSubmitted }) {
  const [sent, setSent] = useState(initial || null);
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("citation_incorrect");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (cat, note = "") => {
    if (!responseId) return;
    setBusy(true);
    try {
      const { data } = await api.post("/feedback", { response_id: responseId, category: cat, comment: note });
      setSent(data.category);
      setOpen(false); setComment("");
      toast.success(cat === "helpful" ? "Thanks for the feedback" : "Report sent to your HR manager's review queue");
      onSubmitted?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send feedback"); }
    finally { setBusy(false); }
  };

  const label = FEEDBACK_OPTIONS.find((o) => o.id === sent)?.label;
  return (
    <div data-testid={`feedback-bar-${responseId}`} className="mt-3 pt-2 border-t border-dashed border-[#FBD38D] flex flex-wrap items-center gap-1.5 text-xs">
      <span className="text-[10px] uppercase tracking-widest font-bold text-[#7C3F00] mr-1">Was this useful?</span>
      <Button size="sm" variant="ghost" disabled={busy} data-testid="feedback-helpful" onClick={() => submit("helpful")} className={`h-7 px-2 ${sent === "helpful" ? "bg-emerald-100 text-emerald-800" : "text-slate-600"}`}><ThumbsUp className="w-3.5 h-3.5 mr-1" /> Helpful</Button>
      <Button size="sm" variant="ghost" disabled={busy} data-testid="feedback-not-helpful" onClick={() => submit("not_helpful")} className={`h-7 px-2 ${sent === "not_helpful" ? "bg-rose-100 text-rose-800" : "text-slate-600"}`}><ThumbsDown className="w-3.5 h-3.5 mr-1" /> Not helpful</Button>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button size="sm" variant="ghost" data-testid="feedback-report" className="h-7 px-2 text-slate-600"><Flag className="w-3.5 h-3.5 mr-1" /> Report an issue</Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-3 space-y-2" onClick={(e) => e.stopPropagation()}>
          <div className="text-xs font-bold text-[#0A2A5E]">What's wrong with this response?</div>
          <div className="space-y-1">
            {FEEDBACK_OPTIONS.filter((o) => !["helpful", "not_helpful"].includes(o.id)).map((o) => (
              <label key={o.id} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                <input type="radio" name={`fb-${responseId}`} data-testid={`feedback-option-${o.id}`} checked={category === o.id} onChange={() => setCategory(o.id)} className="accent-[#0A2A5E]" /> {o.label}
              </label>
            ))}
          </div>
          <Textarea data-testid="feedback-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Optional details (which citation, which policy…)" className="text-xs min-h-[60px]" />
          <Button size="sm" data-testid="feedback-submit" disabled={busy} onClick={() => submit(category, comment)} className="w-full bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white h-8">Send report</Button>
        </PopoverContent>
      </Popover>
      {sent && <span data-testid="feedback-status" className="ml-auto inline-flex items-center gap-1 text-[11px] text-emerald-700 font-semibold"><Check className="w-3 h-3" /> Recorded: {label}</span>}
    </div>
  );
}
